// PRIMARY_BG, not the `--brand-primary` CSS variable: these values are serialised
// into the exported email HTML, which is read outside the browser where no custom
// property exists. brand.js calls this out explicitly.
import { PRIMARY_BG } from '../../../../theme/brand'

export const BLOCK_LABELS = {
  headerBar: 'Header Bar',
  dropShadow: 'Drop Shadow',
  story: 'Story',
  ampForm: 'AMP Form',
  countdown: 'Countdown Timer',
  heading: 'Heading',
  text: 'Text',
  image: 'Image',
  button: 'Button',
  video: 'Video',
  divider: 'Divider',
  spacer: 'Spacer',
  social: 'Social Links',
  footer: 'Footer',
  html: 'HTML',
  columns: 'Columns',
}

export const CONTENT_BLOCK_LIST = [
  { type: 'headerBar', label: 'Header Bar',   color: '#0d9488' },
  { type: 'heading',  label: 'Heading',      color: '#6366f1' },
  { type: 'text',     label: 'Text',          color: '#8b5cf6' },
  { type: 'image',    label: 'Image',         color: '#ec4899' },
  { type: 'button',   label: 'Button',        color: '#2563eb' },
  { type: 'countdown', label: 'Countdown Timer', color: '#0891b2' },
  { type: 'video',    label: 'Video',         color: '#ef4444' },
  { type: 'divider',  label: 'Divider',       color: '#6b7280' },
  { type: 'dropShadow', label: 'Drop Shadow', color: '#475569' },
  { type: 'spacer',   label: 'Spacer',        color: '#10b981' },
  { type: 'social',   label: 'Social Links',  color: '#3b82f6' },
  { type: 'story',    label: 'Story',         color: '#db2777' },
  { type: 'ampForm',  label: 'AMP Form',      color: '#7c3aed' },
  { type: 'footer',   label: 'Footer',        color: '#64748b' },
  { type: 'html',     label: 'HTML',          color: '#0ea5e9' },
]

export const LAYOUT_BLOCK_LIST = [
  { type: 'columns', label: '1 Column',  color: '#7c3aed', propsOverride: { columns: 1 } },
  { type: 'columns', label: '2 Columns', color: '#7c3aed', propsOverride: { columns: 2 } },
  { type: 'columns', label: '3 Columns', color: '#7c3aed', propsOverride: { columns: 3 } },
]

// Drop-shadow presets for the Header bar (box-shadow; renders on canvas + modern
// email clients — Outlook ignores box-shadow, which is expected/acceptable).
export const HEADER_SHADOW_PRESETS = {
  none: '',
  soft: '0 2px 8px rgba(0,0,0,0.08)',
  medium: '0 6px 18px rgba(0,0,0,0.12)',
  strong: '0 10px 28px rgba(0,0,0,0.20)',
}

// Standalone "Drop shadow" element — a thin decorative strip whose soft radial
// shadow makes the section above look elevated. Light/Medium/Dark = intensity.
export const SHADOW_STRIP_PRESETS = {
  light:  { color: 'rgba(0,0,0,0.10)', height: 14 },
  medium: { color: 'rgba(0,0,0,0.18)', height: 18 },
  dark:   { color: 'rgba(0,0,0,0.30)', height: 22 },
}
// The gradient that paints the cast shadow (darkest just under the section, fading down).
export const shadowStripGradient = (key) => {
  const s = SHADOW_STRIP_PRESETS[key] || SHADOW_STRIP_PRESETS.medium
  return `radial-gradient(ellipse 70% 100% at 50% 0%, ${s.color} 0%, rgba(0,0,0,0) 72%)`
}

// Objects with name so the PropertiesPanel can label them
export const GRADIENT_PRESETS = [
  { id: '1',  name: 'Ocean Blue',     value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: '2',  name: 'Sunset',         value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: '3',  name: 'Sky',            value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: '4',  name: 'Royal',          value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { id: '5',  name: 'Warm Flame',     value: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)' },
  { id: '6',  name: 'Night',          value: 'linear-gradient(135deg, #2e3192 0%, #1bffff 100%)' },
  { id: '7',  name: 'Spring',         value: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)' },
  { id: '8',  name: 'Winter',         value: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)' },
  { id: '9',  name: 'Grass',          value: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)' },
  { id: '10', name: 'Azure',          value: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)' },
  { id: '11', name: 'Silver',         value: 'linear-gradient(135deg, #cfd9df 0%, #e2ebf0 100%)' },
  { id: '12', name: 'Deep Blue',      value: 'linear-gradient(135deg, #667db6 0%, #0082c8 100%)' },
]

// Each entry: { name, value (CSS), google (bool), category }
// `value` is what gets stored in emailSettings.fontFamily and block props
export const FONT_FAMILIES = [
  // ── System fonts ──────────────────────────────────────────────────────────
  { name: 'Arial',         value: 'Arial, Helvetica, sans-serif',          google: false, category: 'System' },
  { name: 'Georgia',       value: 'Georgia, "Times New Roman", serif',      google: false, category: 'System' },
  { name: 'Trebuchet MS',  value: '"Trebuchet MS", sans-serif',             google: false, category: 'System' },
  { name: 'Verdana',       value: 'Verdana, Geneva, sans-serif',            google: false, category: 'System' },
  { name: 'Tahoma',        value: 'Tahoma, Geneva, sans-serif',             google: false, category: 'System' },
  { name: 'Courier New',   value: '"Courier New", Courier, monospace',      google: false, category: 'System' },
  // ── Google Fonts — Sans Serif ──────────────────────────────────────────────
  { name: 'Inter',            value: "'Inter', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Roboto',           value: "'Roboto', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Open Sans',        value: "'Open Sans', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Lato',             value: "'Lato', sans-serif",               google: true, category: 'Sans Serif' },
  { name: 'Poppins',          value: "'Poppins', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Montserrat',       value: "'Montserrat', sans-serif",         google: true, category: 'Sans Serif' },
  { name: 'Nunito',           value: "'Nunito', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Raleway',          value: "'Raleway', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'DM Sans',          value: "'DM Sans', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Outfit',           value: "'Outfit', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Manrope',          value: "'Manrope', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Work Sans',        value: "'Work Sans', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Rubik',            value: "'Rubik', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Karla',            value: "'Karla', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Mulish',           value: "'Mulish', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Barlow',           value: "'Barlow', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Heebo',            value: "'Heebo', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Sora',             value: "'Sora', sans-serif",               google: true, category: 'Sans Serif' },
  { name: 'Urbanist',         value: "'Urbanist', sans-serif",           google: true, category: 'Sans Serif' },
  { name: 'Plus Jakarta Sans', value: "'Plus Jakarta Sans', sans-serif", google: true, category: 'Sans Serif' },
  { name: 'Space Grotesk',    value: "'Space Grotesk', sans-serif",      google: true, category: 'Sans Serif' },
  { name: 'IBM Plex Sans',    value: "'IBM Plex Sans', sans-serif",      google: true, category: 'Sans Serif' },
  { name: 'Noto Sans',        value: "'Noto Sans', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'PT Sans',          value: "'PT Sans', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Cabin',            value: "'Cabin', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Josefin Sans',     value: "'Josefin Sans', sans-serif",       google: true, category: 'Sans Serif' },
  { name: 'Quicksand',        value: "'Quicksand', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Comfortaa',        value: "'Comfortaa', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Archivo',          value: "'Archivo', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Epilogue',         value: "'Epilogue', sans-serif",           google: true, category: 'Sans Serif' },
  { name: 'Figtree',          value: "'Figtree', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Public Sans',      value: "'Public Sans', sans-serif",        google: true, category: 'Sans Serif' },
  { name: 'Red Hat Display',  value: "'Red Hat Display', sans-serif",    google: true, category: 'Sans Serif' },
  { name: 'Titillium Web',    value: "'Titillium Web', sans-serif",      google: true, category: 'Sans Serif' },
  { name: 'Fira Sans',        value: "'Fira Sans', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Hind',             value: "'Hind', sans-serif",               google: true, category: 'Sans Serif' },
  { name: 'Assistant',        value: "'Assistant', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Catamaran',        value: "'Catamaran', sans-serif",          google: true, category: 'Sans Serif' },
  { name: 'Exo 2',            value: "'Exo 2', sans-serif",              google: true, category: 'Sans Serif' },
  { name: 'Oxygen',           value: "'Oxygen', sans-serif",             google: true, category: 'Sans Serif' },
  { name: 'Jost',             value: "'Jost', sans-serif",               google: true, category: 'Sans Serif' },
  { name: 'Baloo 2',          value: "'Baloo 2', sans-serif",            google: true, category: 'Sans Serif' },
  { name: 'Fredoka',          value: "'Fredoka', sans-serif",            google: true, category: 'Sans Serif' },
  // ── Google Fonts — Serif ──────────────────────────────────────────────────
  { name: 'Playfair Display', value: "'Playfair Display', serif",        google: true, category: 'Serif' },
  { name: 'Merriweather',     value: "'Merriweather', serif",            google: true, category: 'Serif' },
  { name: 'Lora',             value: "'Lora', serif",                    google: true, category: 'Serif' },
  { name: 'PT Serif',         value: "'PT Serif', serif",                google: true, category: 'Serif' },
  { name: 'Crimson Text',     value: "'Crimson Text', serif",            google: true, category: 'Serif' },
  { name: 'Libre Baskerville', value: "'Libre Baskerville', serif",      google: true, category: 'Serif' },
  { name: 'EB Garamond',      value: "'EB Garamond', serif",             google: true, category: 'Serif' },
  { name: 'Cormorant',        value: "'Cormorant', serif",               google: true, category: 'Serif' },
  { name: 'Bitter',           value: "'Bitter', serif",                  google: true, category: 'Serif' },
  { name: 'Domine',           value: "'Domine', serif",                  google: true, category: 'Serif' },
  { name: 'Noto Serif',       value: "'Noto Serif', serif",              google: true, category: 'Serif' },
  { name: 'Vollkorn',         value: "'Vollkorn', serif",                 google: true, category: 'Serif' },
  { name: 'Spectral',         value: "'Spectral', serif",                 google: true, category: 'Serif' },
  { name: 'Zilla Slab',       value: "'Zilla Slab', serif",               google: true, category: 'Serif' },
  { name: 'Cardo',            value: "'Cardo', serif",                    google: true, category: 'Serif' },
  { name: 'Alegreya',         value: "'Alegreya', serif",                 google: true, category: 'Serif' },
  { name: 'Frank Ruhl Libre', value: "'Frank Ruhl Libre', serif",         google: true, category: 'Serif' },
  // ── Google Fonts — Display ─────────────────────────────────────────────────
  { name: 'Bebas Neue',       value: "'Bebas Neue', sans-serif",          google: true, category: 'Display' },
  { name: 'Anton',            value: "'Anton', sans-serif",               google: true, category: 'Display' },
  { name: 'Righteous',        value: "'Righteous', sans-serif",           google: true, category: 'Display' },
  { name: 'Alfa Slab One',    value: "'Alfa Slab One', serif",            google: true, category: 'Display' },
  { name: 'Passion One',      value: "'Passion One', sans-serif",         google: true, category: 'Display' },
  { name: 'Archivo Black',    value: "'Archivo Black', sans-serif",       google: true, category: 'Display' },
  { name: 'Abril Fatface',    value: "'Abril Fatface', serif",            google: true, category: 'Display' },
  { name: 'Oswald',           value: "'Oswald', sans-serif",              google: true, category: 'Display' },
  // ── Google Fonts — Handwriting ─────────────────────────────────────────────
  { name: 'Pacifico',         value: "'Pacifico', cursive",               google: true, category: 'Handwriting' },
  { name: 'Lobster',          value: "'Lobster', cursive",                google: true, category: 'Handwriting' },
  { name: 'Dancing Script',   value: "'Dancing Script', cursive",         google: true, category: 'Handwriting' },
  { name: 'Caveat',           value: "'Caveat', cursive",                 google: true, category: 'Handwriting' },
  { name: 'Satisfy',          value: "'Satisfy', cursive",                google: true, category: 'Handwriting' },
  { name: 'Great Vibes',      value: "'Great Vibes', cursive",            google: true, category: 'Handwriting' },
  { name: 'Sacramento',       value: "'Sacramento', cursive",             google: true, category: 'Handwriting' },
  { name: 'Kalam',            value: "'Kalam', cursive",                  google: true, category: 'Handwriting' },
  { name: 'Shadows Into Light', value: "'Shadows Into Light', cursive",   google: true, category: 'Handwriting' },
  { name: 'Indie Flower',     value: "'Indie Flower', cursive",           google: true, category: 'Handwriting' },
  { name: 'Permanent Marker', value: "'Permanent Marker', cursive",       google: true, category: 'Handwriting' },
  // ── Google Fonts — Monospace ──────────────────────────────────────────────
  { name: 'Source Code Pro',  value: "'Source Code Pro', monospace",     google: true, category: 'Monospace' },
  { name: 'JetBrains Mono',   value: "'JetBrains Mono', monospace",      google: true, category: 'Monospace' },
  { name: 'IBM Plex Mono',    value: "'IBM Plex Mono', monospace",       google: true, category: 'Monospace' },
  { name: 'Space Mono',       value: "'Space Mono', monospace",          google: true, category: 'Monospace' },
  { name: 'Roboto Mono',      value: "'Roboto Mono', monospace",         google: true, category: 'Monospace' },
  { name: 'Fira Code',        value: "'Fira Code', monospace",           google: true, category: 'Monospace' },
  { name: 'Ubuntu Mono',      value: "'Ubuntu Mono', monospace",         google: true, category: 'Monospace' },
  { name: 'Inconsolata',      value: "'Inconsolata', monospace",         google: true, category: 'Monospace' },
]

// Google Fonts URL for a single font name (weights 400;500;600;700)
export function googleFontUrl(fontName) {
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`
}

// Inject a Google Font <link> into the document once (idempotent)
export function loadGoogleFont(fontName) {
  const id = `gf-${fontName.replace(/\s+/g, '-').toLowerCase()}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = googleFontUrl(fontName)
  document.head.appendChild(link)
}

// Return the FONT_FAMILIES entry whose value matches a stored CSS string
export function findFontEntry(cssValue) {
  return FONT_FAMILIES.find(f => f.value === cssValue) || null
}

export const VARIABLES = [
  { id: '1', label: 'Customer Name', token: '$name',   category: 'customer' },
  { id: '2', label: 'Mobile Number', token: '$mobile', category: 'customer' },
  { id: '3', label: 'Email Address', token: '$email',  category: 'customer' },
]

export const STOCK_IMAGES = [
  { id: '1', name: 'Product Shot',        category: 'Products',    url: 'https://images.unsplash.com/photo-1546868871-7041f2a55e12?w=600&q=80' },
  { id: '2', name: 'Team Collaboration',  category: 'Business',    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80' },
  { id: '3', name: 'Modern Workspace',    category: 'Office',      url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80' },
  { id: '4', name: 'Online Shopping',     category: 'Ecommerce',   url: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&q=80' },
  { id: '5', name: 'Tech Innovation',     category: 'Technology',  url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80' },
  { id: '6', name: 'Customer Service',    category: 'Business',    url: 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?w=600&q=80' },
  { id: '7', name: 'Sale Banner',         category: 'Marketing',   url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&q=80' },
  { id: '8', name: 'Newsletter',          category: 'Marketing',   url: 'https://images.unsplash.com/photo-1587826080692-f439cd0b70da?w=600&q=80' },
  { id: '9', name: 'Social Media',        category: 'Marketing',   url: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&q=80' },
]

let _colIdCounter = 0
export const genColId = () => `col_${++_colIdCounter}_${Math.random().toString(36).slice(2, 6)}`

export function makeColumnDefs(count) {
  return Array.from({ length: count }, () => ({
    id: genColId(),
    backgroundColor: '',
    blocks: [],
  }))
}

export const DEFAULT_BLOCK_PROPS = {
  headerBar: {
    // Layout presets (parity with Klaviyo Header bar; Klaviyo defaults to stacked)
    desktopLayout: 'logo_stacked',     // logo_stacked | logo_inline | logo_centered | links_only
    mobileLayout: 'stack_all',         // stack_all | inline
    useSameLogoAsDesktop: true,
    // Logo
    logoSrc: '',
    logoAlt: 'Logo',
    logoLink: '',
    logoTrackingId: '',                // derived the moment logoLink is first set (empty until then)
    logoNoTrack: false,
    logoMaxWidth: '',                  // '' → auto, else px number
    logoLinkSpacing: 10,               // gap between logo and links (px)
    logoAlignment: 'left',             // left | center | right
    mobileLogoSrc: '',                 // used when useSameLogoAsDesktop is false
    // Repeatable navigation links / images. `trackingId`/`noTrack` are derived
    // per-link (see trackingTags.js) the moment each row is created — never
    // left empty, since an empty field is a field nobody fills in.
    links: [
      { id: 'hl_1', kind: 'link', label: 'Shop',  url: '#', src: '', alt: '', trackingId: '', noTrack: false },
      { id: 'hl_2', kind: 'link', label: 'About', url: '#', src: '', alt: '', trackingId: '', noTrack: false },
    ],
    // Link text style
    linkFontFamily: 'Arial, Helvetica, sans-serif',
    linkFontSize: 16,
    linkColor: '#1A1A1A',
    linkLetterSpacing: 0,
    linkBold: false,
    linkItalic: false,
    linkUnderline: false,
    linkStrike: false,
    // Link alignment
    linkVAlign: 'middle',              // top | middle | bottom
    linkHAlign: 'auto',                // auto | manual
    linkAlignment: 'right',            // left | center | right (when manual)
    // Link padding
    linkPaddingV: 10,
    linkPaddingH: 10,
    // Block chrome
    cornerRadius: 5,
    borderStyle: 'none',               // none | solid | dashed | dotted
    borderWidth: 0,
    borderColor: '#000000',
    backgroundColor: '#ffffff',
    dropShadow: 'none',                // none | soft | medium | strong
    paddingTop: 9,
    paddingBottom: 9,
    paddingLeft: 18,
    paddingRight: 18,
    // Display
    visibility: 'both',                // both | desktop | mobile
  },
  dropShadow: {
    shadowColor: 'medium',   // light | medium | dark
    backgroundColor: '',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  story: {
    // Selected published web story
    storyId: '',
    storyTitle: '',
    poster: '',              // cover image (HTML fallback + first carousel slide)
    url: '',                 // published story URL (deep-link)
    slides: [],              // [{ img, alt }] per story page → AMP-email carousel
    // Presentation
    width: '100',            // % of content width
    alignment: 'center',
    borderRadius: '12',
    autoplay: true,          // auto-advance the AMP carousel (+ builder preview)
    autoplayDelay: 3.5,      // seconds per slide
    showPlayBadge: true,
    showTitle: true,
    showCta: true,
    ctaLabel: 'View Story',
    // "View Story" is a call-to-action button in the recipient's inbox, so it takes
    // the brand colour like every other button. Still overridable per block.
    ctaBackgroundColor: PRIMARY_BG,
    ctaTextColor: '#ffffff',
    // The poster image and the CTA button are two SEPARATE anchors that happen to
    // point at the same story URL — each needs its own tracking id so clicks on
    // the thumbnail can be told apart from clicks on the button.
    trackingId: '',            // CTA anchor
    noTrack: false,
    thumbnailTrackingId: '',   // poster anchor
    thumbnailNoTrack: false,
    backgroundColor: '',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
  },
  ampForm: {
    title: 'Quick question',
    intro: 'Takes 10 seconds — reply right here.',
    fields: [
      { id: 'q1', type: 'rating', label: 'How likely are you to recommend us?', name: 'nps', required: true },
      { id: 'q2', type: 'textarea', label: 'Anything we can improve?', name: 'comment', required: false },
    ],
    submitLabel: 'Submit',
    // Where Submit goes in NON-AMP clients (Outlook, Apple Mail, Gmail without AMP),
    // which cannot post a form at all. Point it at a hosted copy of this form and the
    // button becomes a normal link everywhere. Empty → button is decorative and the
    // note asks the recipient to reply instead.
    fallbackUrl: '',
    successMessage: 'Thanks — your response was recorded! 🎉',
    // Paints the Submit button (and the card's hairline border at 20% alpha), so it
    // follows the brand rather than a fixed violet.
    accentColor: PRIMARY_BG,
    // backgroundColor = the strip BEHIND the form card (block background).
    // cardBackground  = the form panel itself.
    //
    // ET_028: there was only one control. The card was hardcoded #ffffff in both
    // renderers, so whatever colour you picked showed as a border around a white
    // panel and looked like the styling had been lost on export. Two props make the
    // two surfaces independently settable and identical in editor, preview and inbox.
    backgroundColor: '#faf5ff',
    cardBackground: '#ffffff',
    borderRadius: '12',
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 24,
    paddingRight: 24,
  },
  countdown: {
    // Target: an ISO datetime-local string (e.g. "2026-12-31T23:59"). Empty →
    // exporter defaults to +7 days from send time so a fresh block still ticks.
    targetDate: '',
    // Which units to show, in order (subset of d/h/m/s)
    showDays: true,
    showHours: true,
    showMins: true,
    showSecs: true,
    // Labels under each unit
    showLabels: true,
    labelDays: 'Days',
    labelHours: 'Hours',
    labelMins: 'Mins',
    labelSecs: 'Secs',
    // Style
    size: 'md',              // sm | md | lg
    digitColor: '#111827',
    labelColor: '#6B7280',
    boxColor: '#ffffff',     // the timer panel background (the GIF canvas)
    separatorColor: '#111827',
    lcd: true,               // faint "off" 7-segment ghosting (classic timer look)
    alignment: 'center',
    link: '',                // wrap the timer in a link
    trackingId: '',           // derived the moment `link` is first set
    noTrack: false,
    // Block chrome
    backgroundColor: '',
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 24,
    paddingRight: 24,
  },
  heading: {
    content: 'New Heading',
    tag: 'h2',
    fontFamily: '',
    fontSize: '24',
    fontWeight: '700',
    alignment: 'left',
    color: '#000000',
    backgroundColor: '',
    gradient: '',
    paddingTop: 16,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
  },
  text: {
    content: '<p>Type your text here...</p>',
    fontFamily: '',
    fontSize: '14',
    fontWeight: '400',
    alignment: 'left',
    color: '#333333',
    backgroundColor: '',
    lineHeight: '1.6',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
  },
  image: {
    src: '',
    alt: 'Image',
    width: '100',
    alignment: 'center',
    borderRadius: '0',
    link: '',
    trackingId: '',           // derived the moment `link` is first set
    noTrack: false,
    backgroundColor: '',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  button: {
    label: 'Click Here',
    url: 'https://example.com',
    // Derived from the label at creation time (see trackingTags.js) —
    // frozen after that: renaming the button must NOT change it, or click
    // history silently splits across two ids.
    trackingId: '',
    noTrack: false,
    alignment: 'center',
    // Brand colour, not a hardcoded blue. Only affects NEWLY added buttons —
    // existing blocks carry their own saved backgroundColor and are untouched.
    backgroundColor: PRIMARY_BG,
    textColor: '#ffffff',
    borderRadius: '8',
    fontSize: '16',
    fontWeight: '600',
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 24,
    paddingRight: 24,
    buttonPaddingH: 32,
    buttonPaddingV: 14,
  },
  video: {
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    thumbnailUrl: '',
    // Thumbnail and the "Watch video" caption are separate anchors (both point
    // at the same watch URL) — separate ids so clicks can be told apart.
    trackingId: '',            // "Watch video" caption / play badge overlay
    noTrack: false,
    thumbnailTrackingId: '',   // thumbnail image anchor
    thumbnailNoTrack: false,
    alignment: 'center',
    backgroundColor: '',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
  },
  divider: {
    color: '#E5E7EB',
    thickness: '1',
    style: 'solid',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
  },
  spacer: {
    height: '20',
    backgroundColor: '',
  },
  social: {
    facebook: 'https://facebook.com/yourpage',
    twitter: 'https://twitter.com/yourhandle',
    instagram: 'https://instagram.com/yourhandle',
    linkedin: 'https://linkedin.com/company/yourcompany',
    youtube: 'https://youtube.com/yourchannel',
    facebookEnabled: true,
    twitterEnabled: true,
    instagramEnabled: true,
    linkedinEnabled: true,
    youtubeEnabled: false,
    // Tracking ids are auto-derived from the platform name at EXPORT time
    // (e.g. "social_instagram", or "social_instagram_header"/"_footer" if the
    // same platform repeats elsewhere) — see trackingTags.js. These overrides
    // are empty by default and only surfaced under the panel's "Advanced"
    // disclosure; a non-empty value here wins over the auto-derived one.
    facebookTrackingId: '',
    twitterTrackingId: '',
    instagramTrackingId: '',
    linkedinTrackingId: '',
    youtubeTrackingId: '',
    iconSize: '36',
    iconColor: '',
    alignment: 'center',
    backgroundColor: '',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
    gap: '12',
  },
  footer: {
    // The unsubscribe link used to be baked into this HTML string, which meant
    // it could never carry a tracking id of its own. It is now a discrete
    // field (below), rendered after this content — kept as free text for
    // everything else (company name, address, etc.), same as before.
    content: 'Company Name | Address |',
    unsubscribeLabel: 'Unsubscribe',
    unsubscribeUrl: '#',
    unsubscribeTrackingId: '',
    // Unsubscribe links are excluded from click tracking by default — an
    // unsubscribe click isn't the kind of "engagement" this feature measures,
    // and SES's own docs call this out as the standard link to exclude.
    unsubscribeNoTrack: true,
    fontSize: '12',
    color: '#6B7280',
    alignment: 'center',
    backgroundColor: '#f8f8f8',
    paddingTop: 24,
    paddingBottom: 24,
    paddingLeft: 24,
    paddingRight: 24,
  },
  html: {
    content: '<div style="padding:20px;text-align:center;color:#666;"><!-- Custom HTML here --></div>',
    backgroundColor: '',
  },
  columns: {
    columns: 2,
    gap: '16',
    backgroundColor: '',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    paddingRight: 8,
    columnDefs: [],
  },
}
